import type { CaseStudy } from '../types';

export const uber: CaseStudy = {
  id: 'uber',
  order: 10,
  title: 'Uber 与零工平台',
  english: 'Uber & Platform Work',
  category: 'power',
  era: '2009 — 今',
  region: '美国 / 全球',
  accent: '#7a5b76',
  hook: 'Uber 把“叫车”变成实时匹配，也把“谁承担劳动保障”变成了法律问题。平台的力量不仅在算法，也在它如何定义劳动者。',
  question: '同一批司机，为什么在不同国家会被认定为不同的法律身份？',
  summary:
    'Uber 用补贴和算法建立本地双边市场，把乘客与司机的匹配成本降下来。它同时把司机归类为独立承包人，从而改变最低工资、保险与社保的成本归属。英国、加州与欧盟的不同处理，说明“平台”不是一种自然身份，而是法律与商业共同构造的结果。',
  thesis: '平台经济的关键战场，不只是技术效率，而是「成本由谁承担」的法律定义。',
  quote: '当控制程度接近雇佣，却用合同把成本推给劳动者，监管迟早会要求重新分类。',
  entryNodeIds: ['uber-liquidity', 'uber-subsidy', 'uber-classification'],
  focusNodeId: 'uber-classification',
  lensIds: ['network', 'regulation', 'incentives', 'scarcity', 'commitment'],
  opportunityIds: ['opp-gig-compliance'],
  sourceIds: ['uksc-uber-2021', 'ca-prop22-2020', 'eu-platform-work-2024'],
  facts: [
    {
      text: 'Uber 2009 年成立，通过手机应用连接乘客与司机，并以实时匹配、动态定价和司机评分管理供给。',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      text: '2021 年，英国最高法院在 Uber v Aslam 案中认定，涉案司机属于英国法下的“worker”，有权获得最低工资、带薪休假等权利；这与“employee”不是同一概念。',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      text: '加州 2020 年通过 Prop 22，将符合条件的网约车与外卖司机归为独立承包人，同时设置收入下限与部分福利；欧盟 2024 年通过平台工作指令，推动算法管理与身份认定的改善。',
      sourceIds: ['ca-prop22-2020', 'eu-platform-work-2024'],
    },
  ],
  nodes: [
    {
      id: 'uber',
      caseId: 'uber',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'Uber',
      summary: '用实时匹配与补贴重建城市出行市场。',
      detail:
        'Uber 不是第一家出租车公司，也不是第一家做 App 的公司。它把定位、支付、评分、动态定价和司机供给放进同一个实时系统，降低了乘客叫车与司机接单的摩擦。与此同时，它把司机定义为独立承包人，改变了劳动成本的承担方式。',
      boundary: '不同城市、不同业务（网约车、外卖、货运）的监管与劳动安排差异很大。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      id: 'uber-liquidity',
      caseId: 'uber',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '双边流动性：等待时间越短，需求越多',
      summary: '更多司机缩短接驾时间，更好体验又吸引更多乘客。',
      detail:
        '网约车的核心体验是“现在就能走”。司机密度提高，平均接驾时间下降；乘客体验改善，订单增加；订单增加又让司机更容易接到活。这个循环有强烈的本地性：一个城市、一个时段的市场，不能靠另一个城市的供给解决。',
      boundary: '如果司机多归属、乘客价格敏感或城市拥堵严重，网络效应会减弱。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      id: 'uber-subsidy',
      caseId: 'uber',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '补贴战：用资本买临界规模',
      summary: '同时补贴乘客与司机，快速达到本地密度，再期待网络效应回收成本。',
      detail:
        '在多个城市同时开城，需要大量司机与乘客。补贴可以加速临界规模的到来，但也会吸引只对补贴敏感的供给与需求。停止补贴后，如果留存不足，之前买来的规模会迅速蒸发。',
      boundary: '补贴可以建立真实习惯与网络，但前提是留存和单位经济能在补贴退出后站住。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      id: 'uber-classification',
      caseId: 'uber',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '身份分类：成本由谁承担',
      summary: '独立承包人与雇员/worker 的法律区别，决定了最低工资、保险、社保与解雇保护由谁负责。',
      detail:
        '平台通常主张司机是独立承包人：可以自由上线、自带车辆、自负成本。但法院会看实际控制程度：平台是否定价、分配订单、评分、惩罚、限制接单。当控制接近雇佣，法律可能要求平台承担部分雇佣成本。',
      boundary: '零工工作并不必然等于错误分类；关键在于控制、议价权与风险承担的真实分布。',
      counter: '一些高度专业的自由职业者确实拥有定价权与客户选择权，独立承包人身份可能更合适。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021', 'ca-prop22-2020'],
    },
    {
      id: 'uber-algorithm',
      caseId: 'uber',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '算法管理：没有老板的控制',
      summary: '派单、定价、评分与封禁规则由系统执行，司机感受到控制，却缺少传统雇佣关系中的程序保护。',
      detail:
        '平台不需要工头，也能通过派单权重、激励机制、评分和账号封禁影响司机行为。算法管理可以提高效率，但规则不透明、申诉渠道弱时，司机很难知道为什么收入下降或被限制。欧盟平台工作指令正是针对这一治理缺口。',
      boundary: '算法规则可以公开、可申诉、由人工复核；技术本身不决定治理质量。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['eu-platform-work-2024'],
    },
    {
      id: 'uber-condition',
      caseId: 'uber',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：智能手机、支付与城市密度',
      summary: '定位、移动支付、评分系统和高密度城市，使实时匹配在商业上可行。',
      detail:
        '没有智能手机与移动支付，实时匹配和自动结算无法低成本完成；没有足够的人口密度，等待时间与空驶率会让单位经济恶化。平台模式是技术条件与城市场景的组合。',
      boundary: '低密度地区、现金支付为主或监管严格的市场，平台经济模型会不同。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021'],
    },
    {
      id: 'uber-outcome',
      caseId: 'uber',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：新市场与持续的法律战',
      summary: '网约车成为城市基础设施，但劳动身份、定价与算法规则仍在不同法域重写。',
      detail:
        'Uber 改变了城市出行与就业形态，也引发了对司机收入、保险、拥堵和算法公平的持续争论。英国、加州与欧盟的不同路径说明：同一套技术可以在不同制度下形成不同的成本与权利分配。',
      boundary: '平台的市场地位与盈利能力因地区而异，不能只用“颠覆者”或“剥削者”一个标签概括。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021', 'ca-prop22-2020', 'eu-platform-work-2024'],
    },
    {
      id: 'uber-risk',
      caseId: 'uber',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：低成本建立在身份模糊上',
      summary: '如果监管重新分类，平台的人工成本、保险与排班规则会被重写。',
      detail:
        '平台的经济模型部分依赖于不支付传统雇佣成本。一旦法院或立法改变分类，单位经济会显著变化；平台可能提高价格、减少供给或改变合同。对司机，重新分类可能带来保障，也可能减少灵活性与进入机会。',
      boundary: '不同国家的劳动法差异很大；把英国判决当作全球统一结论会误判。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['uksc-uber-2021', 'eu-platform-work-2024'],
    },
    {
      id: 'uber-opp',
      caseId: 'uber',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：为“身份成本”做基础设施',
      summary: '合规、保险、薪酬、申诉与福利账户，正在从平台内部成本变成可外包的服务。',
      detail:
        '随着平台工作监管收紧，市场需要能处理多法域身份、工时、保险、税务与申诉的系统。对普通创业者，可以从一个细分人群切入：自由职业者的收入与保障账户、小型平台的合规代运营、司机/骑手的证据留存与法律援助。',
      boundary: '合规服务受牌照与法律责任约束；客户付费意愿取决于执法强度，政策变化风险高。',
      evidence: 'hypothesis',
      confidence: 'medium',
      sourceIds: ['eu-platform-work-2024', 'ca-prop22-2020'],
    },
  ],
  edges: [
    {
      id: 'uber-e1',
      caseId: 'uber',
      from: 'uber',
      to: 'uber-liquidity',
      label: '实时匹配',
      explanation: '定位与支付让乘客和司机可以在同一时刻完成匹配与结算。',
      tone: 'cause',
    },
    {
      id: 'uber-e2',
      caseId: 'uber',
      from: 'uber',
      to: 'uber-subsidy',
      label: '买临界规模',
      explanation: '用补贴同时吸引司机与乘客，尽快达到本地网络密度。',
      tone: 'cause',
    },
    {
      id: 'uber-e3',
      caseId: 'uber',
      from: 'uber-liquidity',
      to: 'uber-outcome',
      label: '体验改善',
      explanation: '更短等待与更少空驶提高乘客体验，也提高司机接单效率。',
      tone: 'cause',
    },
    {
      id: 'uber-e4',
      caseId: 'uber',
      from: 'uber-subsidy',
      to: 'uber-outcome',
      label: '规模换市场',
      explanation: '资本补贴加速市场教育，但留存与单位经济决定能否持续。',
      tone: 'cause',
    },
    {
      id: 'uber-e5',
      caseId: 'uber',
      from: 'uber-subsidy',
      to: 'uber-classification',
      label: '成本外部化',
      explanation: '独立承包人身份让平台避免部分雇佣成本，补贴战的账本因此更容易成立。',
      tone: 'cause',
    },
    {
      id: 'uber-e6',
      caseId: 'uber',
      from: 'uber-classification',
      to: 'uber-outcome',
      label: '法律重写成本',
      explanation: '当法院或立法重新分类，平台的价格、供给与利润结构都要调整。',
      tone: 'cause',
    },
    {
      id: 'uber-e7',
      caseId: 'uber',
      from: 'uber-algorithm',
      to: 'uber-outcome',
      label: '控制与申诉',
      explanation: '算法管理提高匹配效率，也把规则透明与程序正义推到前台。',
      tone: 'cause',
    },
    {
      id: 'uber-e8',
      caseId: 'uber',
      from: 'uber-condition',
      to: 'uber-outcome',
      label: '技术与城市前提',
      explanation: '智能手机、支付与人口密度，使实时匹配具备商业可行性。',
      tone: 'enable',
    },
    {
      id: 'uber-e9',
      caseId: 'uber',
      from: 'uber-outcome',
      to: 'uber-risk',
      label: '身份风险',
      explanation: '平台越像雇主，重新分类的法律与财务压力越大。',
      tone: 'risk',
    },
    {
      id: 'uber-e10',
      caseId: 'uber',
      from: 'uber-risk',
      to: 'uber-opp',
      label: '合规外包需求',
      explanation: '监管复杂度上升，会把身份、保障与申诉变成专业服务市场。',
      tone: 'enable',
    },
  ],
};

